import { useEffect } from 'react'
import { PBCC_PREMIUM } from '../config/siteMode.js'

// ── Board auto-update ─────────────────────────────────────────────────────
// TVs must always run the current deploy with ZERO manual refresh. Each build
// gives Vite's entry bundle a content-hashed name (/assets/index-<hash>.js).
// The running page's <script type="module"> tag carries the hash THIS tab
// loaded; the live index.html (served no-cache) carries the hash of the CURRENT
// deploy. When they differ, a newer build is live → reload.
//
// Only the display / board / TV routes auto-reload — never the admin app or
// interactive player pages (so nobody loses work mid-entry). index.html is
// no-cache and no service worker remains, so the fetch always sees the latest.

const POLL_MS = 15000       // check for a new deploy every 15s
const FIRST_CHECK_MS = 3000 // and once shortly after load
const RELOAD_TARGET_KEY = 'ctl_autoupdate_target' // loop guard

// Routes safe to auto-reload: view-only TV / spectator boards. Intentionally
// EXCLUDES /admin and interactive pages (/photos submit, /calcutta bid,
// /score, /register, /qualify) so a reload never interrupts input.
const BOARD_PREFIXES = ['/tv', '/leaderboard', '/photowall', '/hub', '/map']

function isBoardRoute(path) {
  return BOARD_PREFIXES.some((p) => path === p || path.startsWith(p + '/') || path.startsWith(p + '?') || path === p)
}

// TEST: the token PLAYER app (/play) persists its progress (token + qualifying
// scores) to localStorage and restores on mount, so it's safe to auto-update
// too — players never get stuck on a stale build during qualifying. Club/live
// keep the interactive-pages-excluded behavior.
const PLAYER_PREFIXES = PBCC_PREMIUM ? ['/play'] : []
function isAutoReloadRoute(path) {
  return isBoardRoute(path) || PLAYER_PREFIXES.some((p) => path === p || path.startsWith(p + '/') || path.startsWith(p + '?'))
}

// The entry-bundle hash THIS tab is running (from the loaded <script> tag).
function currentHash() {
  try {
    const el = document.querySelector('script[type="module"][src*="/assets/index-"]')
    const src = el && el.getAttribute('src')
    const m = src && src.match(/index-([A-Za-z0-9_-]+)\.js/)
    return m ? m[1] : null
  } catch {
    return null
  }
}

// The entry-bundle hash of the CURRENT deploy (from the live, no-cache index.html).
async function deployedHash() {
  const res = await fetch('/?_ctlv=' + Date.now(), {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache' },
  })
  if (!res.ok) return null
  const html = await res.text()
  const m = html.match(/\/assets\/index-([A-Za-z0-9_-]+)\.js/)
  return m ? m[1] : null
}

export default function BoardAutoUpdater() {
  useEffect(() => {
    const mine = currentHash()
    if (!mine) return undefined // can't determine our own build → do nothing (safe)

    let stopped = false
    const check = async () => {
      if (stopped) return
      // Only auto-reload the display/board routes (+ the TEST player app).
      if (!isAutoReloadRoute(window.location.pathname || '/')) return
      let deployed
      try {
        deployed = await deployedHash()
      } catch {
        return // network blip — try again next tick
      }
      if (!deployed || deployed === mine) return // up to date

      // New deploy detected. Loop guard: never try to reload to the SAME target
      // twice — if a reload somehow didn't land the new build, don't spin.
      try {
        if (sessionStorage.getItem(RELOAD_TARGET_KEY) === deployed) return
        sessionStorage.setItem(RELOAD_TARGET_KEY, deployed)
      } catch { /* sessionStorage blocked — still reload once below */ }

      try { console.info(`[CTL] new build live (${mine} → ${deployed}) — auto-reloading`) } catch { /* ignore */ }
      window.location.reload()
    }

    const id = setInterval(check, POLL_MS)
    const first = setTimeout(check, FIRST_CHECK_MS)
    const onVisible = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', check)

    return () => {
      stopped = true
      clearInterval(id)
      clearTimeout(first)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', check)
    }
  }, [])

  return null
}
